import {RoundingMethods} from "../../Configuration/GeneralOptions/OptionConstants";
import OptionHelper from "../../Configuration/GeneralOptions/OptionHelper";
import store from "../../store/store";
import Task from "../../entities/task";

export type DurationFormatTypes = 'hms'|'h m s'|'hh:mm'|'hh:mm:ss'|'decimal'|'sdecimal';

/**
 * Take a duration (in days), and truncate to the given minute interval.
 * For example, take 1.34432 days, with a six-minute interval, giving 1.345833333 days.
 * Note that we can not get 0 unless passed an actual 0. Otherwise, we need to round up to the nearest interval.
 *
 * @param {number | null | undefined} duration (seconds * 1000)
 * @param {number} interval
 * @param {string} roundingMethod
 */
export function roundDurationToMinutes(duration: number | null | undefined, interval: number, roundingMethod: string): number {
    // Zeros and nulls return themselves
    if (!duration) return 0;

    // How many minutes is this?
    const minutes = duration / 60000.0;

    // How many interval-periods is that?
    const intervals = minutes / interval;

    // Round up/down, but never less than 1 interval!
    const roundedIntervals = roundingMethod === RoundingMethods.ROUNDING_METHOD_UP
                             ? Math.max(1, Math.ceil(intervals))
                             : roundingMethod === RoundingMethods.ROUNDING_METHOD_DOWN
                               ? Math.max(1, Math.floor(intervals))
                               : Math.max(1, Math.round(intervals));

    // Reconstitute days using rounded intervals
    return (roundedIntervals * interval) * 60000.0;
}

export function roundDurationToSeconds(duration: number, interval: number, roundingMethod: string): number {
    if (!duration) duration = 0;
    return roundDurationToMinutes(duration/60.0, interval, roundingMethod);
}

/**
 * Get hh:mm:ss from duration in ms
 *
 * @param {number} ms - duration in milliseconds
 * @param {DurationFormatTypes} fmt - format as hhh mmm sss
 * @param {Task} [task]
 * @returns {string} - formatted as hh:mm:ss
 * @throws {Error}
 */
export function formatDuration(ms: number = 0, fmt: DurationFormatTypes = 'hh:mm:ss', task?: Task,
                               billingSettings?: {billingIncrement?: number; roundingMethod?: string}): string {
    // Handle negative durations (these were created in error)
    if (ms < 0) ms = -1 * ms;

    // Handle simple cases
    if (ms !== 0 && ms == null) ms = 0;

    // TODO: We are getting nulls for fmt. Make all calls specify the format!
    if (!fmt?.includes('decimal')) {
        const seconds = ms / 1000;

        const h = Math.floor(seconds / 3600); // 3600 seconds in an hour
        const m = Math.floor((seconds - (3600 * h)) / 60); // Subtract whole hours to get remaining minutes
        const s = Math.floor(seconds - (3600 * h) - (60 * m)); // Subtract whole minutes to get remaining seconds

        const padLength = Math.max(2, Math.floor(Math.log10(h)));
        const hh = `${h}`.padStart(padLength, '0');
        const mm = ('00' + m).slice(-2);
        const ss = ('00' + s).slice(-2);

        if (fmt === 'h m s') return `${hh}h ${mm}m ${ss}s`;
        else if (fmt === 'hh:mm') return `${hh}:${mm}`;
        else if (fmt === 'hh:mm:ss') return `${hh}:${mm}:${ss}`;
        else return `${hh}:${mm}:${ss}`;
    }
    else {
        // Going to need the billing increment and rounding method
        const invoiceId = task?.invoiceId;
        const invoice = invoiceId ? store.getState().invoices.get(invoiceId) : null;

        const billingIncrement = Number(billingSettings?.billingIncrement ?? invoice?.billingIncrement ?? OptionHelper.getOptionValueStatic(OptionHelper.BILLING_INCREMENT) ?? 6);
        const roundingMethod: string = billingSettings?.roundingMethod ?? invoice?.roundingMethod ?? (OptionHelper.getOptionValueStatic(OptionHelper.ROUNDING_METHOD) ?? RoundingMethods.ROUNDING_METHOD_MIDDLE) as string;

        // How many significant digits do we need?
        let digits = (billingIncrement / 60).toString().length - 2;
        if (digits > 2) digits = 2;

        // TODO: Once everything is in TypeScript, we can remove the Number.parseFloat() call
        const elapsedTime = task
                            ? (task.isLaborTimeOverridden || task.isTravelTimeOverridden)
                              // @ts-ignore
                              ? task.laborTimeOverride ?? Number.parseFloat(task.travelTimeOverride) ?? 0
                              : task.elapsedTime
                            : ms;

        const hours = (roundDurationToMinutes(elapsedTime, billingIncrement, roundingMethod) / 3600000.0).toFixed(digits);

        if (fmt === 'decimal') return hours + ' hours';
        else return hours;
    }
}
